import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../api";
import { AuthContext } from "../AuthContext";

export default function MekFundedAccounts({ accounts: propAccounts = null, selectedAccountId = null, onSelect = null }) {
  const { token } = useContext(AuthContext);

  const [accounts, setAccounts] = useState(propAccounts || []);
  const [loading, setLoading] = useState(propAccounts == null);
  const [error, setError] = useState("");

  useEffect(() => {
    // If parent provides accounts (including empty array), sync local state
    if (propAccounts != null) {
      setAccounts(propAccounts);
      setLoading(false);
      return;
    }

    // Otherwise, attempt to fetch from API when token is available
    async function loadAccounts() {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/mekfunded/accounts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed loading accounts");
        setAccounts(data.accounts || []);
      } catch (err) {
        setError(err.message || "Failed loading accounts");
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      loadAccounts();
    }
  }, [token, propAccounts]);

  if (loading) {
    return <div className="rounded-3xl bg-white p-6">Loading MekFunded accounts...</div>;
  }

  if (error) {
    return <div className="rounded-3xl bg-red-50 p-6 text-red-700">{error}</div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-indigo-700">My MekFunded Accounts</h2>
        <p className="text-gray-500 mt-1">Manage all connected funded accounts.</p>
      </div>

      {accounts.length === 0 ? (
        <div className="rounded-3xl bg-gray-50 p-8 text-center">
          <p className="font-semibold">No MekFunded accounts connected yet.</p>
          <p className="text-sm text-gray-500 mt-2">Your funded accounts will appear here after claiming an invite.</p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {accounts.map((account) => (
            <AccountCard
              key={account._id}
              account={account}
              selected={String(selectedAccountId) === String(account._id)}
              onSelect={() => onSelect && onSelect(account._id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AccountCard({ account, selected = false, onSelect = null }) {
  const navigate = useNavigate();

  const handleView = () => {
    if (onSelect) onSelect();
    // navigate to trades view so selection is visible in context
    navigate("/app/trades");
  };

  return (
    <div className="relative rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {selected ? (
        <div className="absolute top-4 right-4">
          <span className="inline-block rounded-full bg-indigo-600 px-3 py-1 text-xs text-white font-semibold">Selected</span>
        </div>
      ) : null}

      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-bold text-lg">MekFunded Account</h3>
          <p className="text-sm text-gray-500">{account.accountType || "Trading Account"}</p>
        </div>

        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">{account.status}</span>
      </div>

      <div className="mt-5 space-y-3">
        <Row label="Account ID" value={account.fundedAccountId} />
        <Row label="Phase" value={account.phase} />
        <Row label="MT5 Login" value={account.mt5Login} />
        <Row label="Server" value={account.mt5Server} />
      </div>

      <div className="mt-5 border-t pt-4 text-sm text-gray-500">
        Trade Sync:
        <span className="font-semibold ml-2">{account.tradeSyncEnabled ? "Enabled" : "Disabled"}</span>
      </div>

      <div className="mt-3 flex justify-end">
        <button onClick={handleView} className="px-3 py-1 rounded-lg bg-indigo-600 text-white text-sm font-semibold">View trades</button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold">{value || "-"}</span>
    </div>
  );
}
